import { forwardRef, useEffect, useState, useImperativeHandle } from 'react';
import styled from "styled-components";

import PropSelector from './comps/PropSelector';
import EntryDetail from './aragon/EntryDetail';
import WeatherDetails from './aragon/WeatherDetails';
import WindAnalysis from './aragon/WindAnalysis';
import LandingView from './aragon/LandingView';
import FeedbackModal from './aragon/components/FeedbackModal';
import { Chip } from './aragon/components/Chip';

import { Icon, Tabs, Button, COLORS, Text } from '@ui-component-library';
import { TailwindWrapper } from '@utils/TailwindWrapper';

import { useVisaValdaStore } from './store/VisaValdaStore';
import { useDFR } from './context/DFRContext';
import { useNavigationStore } from './store/NavigationStore';
import { usePropsDataStore } from './store/PropsDataStore';
import { useOpenSubscriptionCheckout } from '@components/Checkout/config/SubscriptionCheckout/SubscriptionCheckoutContext';

import PineTreeIcon from '@assets/icons/pine-tree.svg';

const tabItems = ["Skogskador", "Risker", "I området", "Väder", "Vindanalys"]

const ListPanel = styled.div`
    background-color: ${COLORS['stone-50']};
    border: 1px solid #E2E1E0;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
`

const ListItem = styled.div`
    padding: 12px 16px;
    border-bottom: 1px solid #e6ded5;
    border-left: 1px solid #e6ded5;
    border-right: 1px solid #e6ded5;

    &:hover {
        background-color: ${COLORS['charcoal-alpha-50']};
    }

    &.done {
        background-color: ${COLORS['cloud-100']};

        &:hover {
            background-color: ${COLORS['charcoal-alpha-50']};
        }
    }
`

const BottomTable = styled.div`
    padding: 16px;
    border-bottom: 1px solid #e6ded5;
    border-left: 1px solid #e6ded5;
    border-right: 1px solid #e6ded5;
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;

    &.hover {
        &:hover {
            background-color: ${COLORS['charcoal-alpha-50']};
        }
    }
`

const RoundedContainer = styled.div`
    border-radius: 16px;
`

const FeedbackButton = styled.div`
    margin: 20px 20px 90px;
    @media (min-width: 768px) {
        margin: 20px;
    }
`

const Aragon = forwardRef((props, ref) => {
    const { visaValda, setVisaValda } = useVisaValdaStore();
    const { view, setView } = useNavigationStore()
    const { props: properties, setProps } = usePropsDataStore()

    const hasDFR = properties.some(p => p.dfr)

    const [activeTab, setActiveTab] = useState(view?.tab ? view?.tab : tabItems[0]);
    const [dataLength, setDataLength] = useState({Damage: 5, Risk: 5, News: 5});
    const [landingView, showLandingView] = useState(!properties.some(p => p.dfr && p.active));
    const [showFeedback, setShowFeedback] = useState(false);

    const DFRcontext = useDFR();
    const { openCheckout } = useOpenSubscriptionCheckout();

    useImperativeHandle(ref, () => ({
        handleHistory: () => {
            setActiveTab(history.state.tab ? history.state.tab : tabItems[0])
        }
    }))

    useEffect(() => {
        fetchDataIfMissing()

        let currentView = view
        if(!currentView.tab) {
            currentView.tab = tabItems[0]
            history.replaceState(currentView, '', '');
        }
    }, []);

    useEffect(() => {
        if(visaValda) {
            fetchDataIfMissing()
            showLandingView(!properties.some(p => p.dfr && p.active))
            setDataLength({Damage: 5, Risk: 5, News: 5})
            setVisaValda(false)
        }
    }, [visaValda]);

    const fetchDataIfMissing = () => {
        const activeDfrProps = properties.filter(p => p.active && p.dfr)

        activeDfrProps.forEach(async p => {
            if(!DFRcontext.wind[p.ref]?.month) {
                DFRcontext.getWind(p.ref, p.centroid, 'month')
            }
            
            if(!DFRcontext.wind[p.ref]?.years) {
                DFRcontext.getWind(p.ref, p.centroid, 'years')
            }
            
            if(!DFRcontext.weatherData[p.ref]) {
                DFRcontext.getWeatherData(p.ref, p.designation)
            }

            if(!DFRcontext.windEdgeRisks[p.ref]) {
                DFRcontext.getWindEdgeRisk(p.ref)
            }
        })
    }
    
    const handleTabChange = tab => {
        setActiveTab(tab);
        let newView = {...view, tab: tab}
        history.pushState(newView, '', '')
        setView(newView)
    };

    const handleEntryChange = entry => {
        let newView = {...view, entry: entry}
        history.pushState(newView, '', '')
        setView(newView)
    }

    const ListPanelData = (title, description, key) => {
        const activeProps = properties.filter(p => p.active)
        const isLoading = activeProps.some(p => !p.ready || (p.dfr && p.entriesReady === false))
        const items = DFRcontext.entries.filter(item => item.type == key && activeProps.some(p => p.ref == item.ref)).sort((a,b) => new Date(b.occured.slice(0, 10)) - new Date(a.occured.slice(0, 10)))

        return <>
            <Text variant="text-xl/leading-normal/medium">{title}</Text>

            <Text className='my-4' variant="text-lg/leading-normal/normal">{description}</Text>

            {isLoading &&
            <RoundedContainer className='flex flex-col justify-center items-center bg-stone-50' style={{minHeight: '300px'}}>
                 <Icon name="Loader" size="xlarge" className="animate-spin " />
                <Text variant='text-base/leading-normal/medium' className='mt-4'>Vi hämtar din data...</Text>
            </RoundedContainer>
            } 
            
            {!isLoading && items.length === 0 && <RoundedContainer className='flex justify-center bg-stone-50 p-4 lg:p-5'>
                <div className='flex flex-col justify-center items-center gap-3' style={{maxWidth: '722px'}}>
                    <div className='flex items-center justify-center rounded rounded-full bg-stone-100 mb-4' style={{height: '60px', width: '60px'}}>
                        <PineTreeIcon fill='#2a2e33' width='32px' height='32px' />
                    </div>
                    <Text variant="text-xl/leading-normal/medium" lg="text-2xl/leading-normal/medium" className='text-center mb-2'>Välkommen till skogsövervakning</Text>

                    <Text variant="text-lg/leading-normal/normal" className='text-center mb-4 lg:mb-5'>Det är här du kommer kunna få viktigt information om din skogsfastighet. Allt från skogsskador, risker, händelser som inträffar i närområdet, väderinformation och vindanalys över din skogsfastighet.</Text>

                    <RoundedContainer className='flex bg-white border border-pebble-50 mt-4' style={{padding: '16px'}}>
                        <div style={{width: '32px'}}>
                            <div className='flex justify-center items-center rounded rounded-full bg-green-spruce-50' style={{height: '32px', width: '32px'}}>
                                <Icon name='LightbulbSimple' className="fill-green-spruce-700" size='large' />
                            </div>
                        </div>
                        
                        <div style={{marginLeft: '16px'}}>
                            <Text variant="text-lg/leading-none/medium" className='mb-2'>Bra att  känna till!</Text>

                            <Text variant='text-base/leading-normal/normal'>Vi har nu påbörjat övervakningen på din skogsfastighet. Eftersom tjänsten är ny för dig kan de första uppdateringarna dyka upp inom några timmar eller dagar, beroende på fastighetens läge och om något nytt inträffar i området.</Text>
                        </div>
                    </RoundedContainer>
                </div>
            </RoundedContainer> }
            
            {!isLoading && items.length > 0 &&
                <>
                    <div>
                        <ListPanel>
                            <Text className='p-4' variant="text-base/leading-normal/medium">Aktivitet</Text>
                        </ListPanel>
                        
                        {items.slice(0, dataLength[key]).map(item => {
                            const latestState = item.states.filter(s => !s.deleted)[0]
                            return <ListItem role='button' onClick={() => handleEntryChange(item)} className={`flex justify-between ${(latestState.state == 'done' || !latestState.state) && 'done'}`}>
                                <div className='flex flex-col'>
                                    <Text variant="text-sm/leading-normal/medium">{item.title}</Text>
                                    <Text variant="text-xs/leading-normal/normal">{item.occured.slice(0, 10)}</Text>
                                </div>

                                <div className='flex items-center gap-2'>
                                    <Chip type={latestState.state} />
                                    <Icon name="ChevronDownSimple" size="large" className="-rotate-90" />
                                </div>
                            </ListItem>
                        })}
                    </div>
                    {items.length > dataLength[key] ?
                        <BottomTable role='button' className='flex justify-center hover' onClick={() => setDataLength({...dataLength, [key]: dataLength[key]+5})}>
                            <Text className='mr-2' variant="text-sm/leading-normal/medium">Visa fler</Text>
                            <Icon name="ChevronDownSimple" size="large" />
                        </BottomTable> :
                        <BottomTable className='text-center'>
                            <Text variant="text-sm/leading-normal/medium">Inga fler resultat</Text>
                        </BottomTable>
                    }
                </>
                
            } 
        </>
    }

    const activateDFRProps = () => {
        if(properties.some(p => p.dfr && p.active)) {
            showLandingView(false)
        } else {
            const newProps = properties.map(p => {
                if(p.dfr && p.ready) {
                    return { ...p, active: true, checked: true }
                }
                return p
            })

            setProps(newProps)
            setVisaValda(true)
        }
    }

    return <>
        {!view?.showMap && <FeedbackButton className='fixed bottom-0 end-0 z-[1030] flex justify-end'>
            <Button variant='secondary' onClick={() => setShowFeedback(true)}><Icon name='EditPenSimple' />Lämna feedback</Button>
        </FeedbackButton>}
        
        {landingView && <div className='relative bg-cloud-200'>
            
            {properties.filter(p => p.dfr && p.ready).length > 0 && <div className='container relative p-4' style={{maxWidth: '960px'}}>
                <div className='flex bg-white border border-pebble-50 mt-4' style={{borderRadius: '8px', padding: '12px'}}>
                    <div style={{marginRight: '12px', marginTop: '3px'}}>
                        <Icon name='InfoFilled' className="fill-pebble-500" size='large' />
                    </div>
                    
                    <div>
                        <Text variant="text-xs/leading-normal/medium" className='mb-1'>Skogsövervakning finns inte för alla dom valda fastigheterna</Text>

                        <Text variant="text-xs/leading-normal/normal" className="text-pebble-500">Alla valda fastigheterna har inte tillgång till skogsövervakning. Men du har tillgång till skogsövervakning på dina andra fastigheter. Välj de fastigheter som har  <img className='inline-block' src={`${Window.static_path}img/icons/sparkle.svg`} alt='Sparkle' height='15px' width='15px' />  bredvid sig.</Text>

                        {hasDFR && <Button className='mt-2' onClick={() => activateDFRProps()}>{properties.some(p => p.dfr && p.active) ? 'Tillbaka till skogsövervakning' : 'Se övervakade fastigheter'}</Button>}
                    </div>
                </div>
            </div>}
            
            <LandingView />
        </div>}

        {!landingView && view.entry && <EntryDetail entry={view.entry} />}

        {!landingView && !view.entry && <div className='relative p-4' style={{maxWidth: '960px'}}>

            {properties.filter(p => !p.dfr && p.active).length > 0 && <div className='flex bg-cloud-200 border border-pebble-50 mt-4' style={{borderRadius: '8px', padding: '12px'}}>
                <div style={{marginRight: '12px', marginTop: '3px'}}>
                    <Icon name='InfoFilled' className="fill-pebble-500" size='large' />
                </div>
                
                <div>
                    <Text variant="text-xs/leading-normal/medium" className='mb-1'>{properties.filter(p => !p.dfr && p.active).length == 1 ? properties.filter(p => !p.dfr && p.active)[0].designation : 'Fler valda fastigheter'} har inte skogsövervakning aktiverat. </Text>

                    <Text variant="text-xs/leading-normal/normal" className="text-pebble-500">Aktivera tjänsten för att kunna följa förändringar och risker i området – precis som på dina andra fastigheter</Text>

                    <div className='flex mt-2'>
                        <Button onClick={openCheckout}>Bli pluskund</Button>
                        <Button variant='secondary' className='ml-2' onClick={() => showLandingView(true)}>Läs mer</Button>
                    </div>
                </div>
            </div>}

            <div className='mt-4'>
                <Tabs tabs={tabItems.map(t => ({label: t, value: t}))} onChange={handleTabChange} value={activeTab} />
            </div>
            
            <div className='mt-4'>
                {activeTab == 'Skogskador' && ListPanelData('Skogskador', 'Här nedan ser du resultatet från den senaste satellitövervakningen av din skog. Med hjälp av avancerad fjärranalys upptäcker vi förändringar i skogen – till exempel avverkning, stormskador eller andra avvikelser – så att du får full överblick utan att behöva gå ett enda steg.', 'Damage')}
                {activeTab == 'Risker' && ListPanelData('Risker', 'Som skogsägare är det värdefullt att känna till aktuella risker som kan påverka din skog. Genom att hålla dig uppdaterad på aktuella risker får du bättre beslutsunderlag och kan skydda både värdet och hälsan i din skog.', 'Risk')}
                {activeTab == 'I området' && ListPanelData('I området', 'Funktionen I området ger dig som skogsägare en snabb överblick över viktiga händelser i området kring din fastighet.', 'News')}
                
                {activeTab == 'Väder' && <WeatherDetails />}
                {activeTab == 'Vindanalys' && <WindAnalysis />}
            </div>
        </div>}
        
        <FeedbackModal show={showFeedback} setShow={setShowFeedback} />
    </>
})

export default Aragon;